import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { SignSheet } from "@/components/wallet/SignSheet";
import { useWallet } from "@/lib/wallet/store";
import { quoteFlatFee } from "@/lib/wallet/fees";
import { nftsQuery } from "@/lib/wallet/queries";
import { addressFingerprint, truncateAddress } from "@/lib/wallet/format";
import { isValidAddress, resolveMediaUrl, type NftItem } from "@/lib/wallet/xrpl";

export const Route = createFileRoute("/nfts")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "NFTs — Cobalt Wallet" },
      {
        name: "description",
        content:
          "View and transfer your XLS-20 NFTs held on the XRP Ledger, with locally signed transfer offers.",
      },
      { property: "og:title", content: "NFTs — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Your XLS-20 collection, read straight from the validated ledger.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <NftScreen />
    </Gate>
  ),
});

function NftScreen() {
  const { address, submit } = useWallet();
  const nfts = useQuery(nftsQuery(address));
  const [selected, setSelected] = useState<NftItem | null>(null);
  const [destination, setDestination] = useState("");
  const [valid, setValid] = useState<boolean | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const transfer = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const result = await submit({
        TransactionType: "NFTokenCreateOffer",
        Account: address ?? "",
        NFTokenID: selected.id,
        Destination: destination.trim(),
        Amount: "0",
        Flags: 1, // tfSellNFToken — a zero-price sell offer directed at the recipient
      }, { fee: quoteFlatFee("NFT transfer") });
      if (result.succeeded) {
        toast.success("Transfer offer created. The recipient accepts it to complete the move.");
        setSheetOpen(false);
        setSelected(null);
        setDestination("");
        setValid(null);
        void nfts.refetch();
      } else {
        toast.error(`Ledger rejected: ${result.result}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Transfer failed.");
    } finally {
      setBusy(false);
    }
  };

  const items = nfts.data ?? [];

  return (
    <div className="animate-rise space-y-4">
      <div>
        <h1 className="text-[18px] font-medium tracking-tight">Collection</h1>
        <p className="readout mt-1 text-[10px] text-faint">
          {items.length} XLS-20 token{items.length === 1 ? "" : "s"} held by this account
        </p>
      </div>

      {items.length === 0 ? (
        <div className="panel p-4">
          <p className="readout text-[11px] leading-relaxed text-faint">
            No NFTs in this account yet. Anything minted to or transferred to your address appears
            here once the ledger validates it.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {items.map((nft) => {
            const media = resolveMediaUrl(nft.uri);
            return (
              <button
                key={nft.id}
                type="button"
                onClick={() => {
                  setSelected(nft);
                  setDestination("");
                  setValid(null);
                }}
                className={`panel overflow-hidden text-left transition-colors ${
                  selected?.id === nft.id ? "border-accent" : "hover:bg-surface2"
                }`}
              >
                <div className="aspect-square bg-elevated">
                  {media ? (
                    <img
                      src={media}
                      alt={`NFT ${nft.id.slice(0, 8)}`}
                      loading="lazy"
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="grid size-full place-items-center">
                      <span className="label-instrument">no media</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <p className="readout truncate text-[11px]">{nft.id.slice(0, 12)}…</p>
                  <p className="readout mt-1 text-[10px] text-faint">
                    taxon {nft.taxon} · #{nft.serial}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected ? (
        <section className="panel space-y-3 p-4">
          <p className="label-instrument">Transfer NFToken</p>
          <p className="readout break-all text-[10px] text-muted">{selected.id}</p>
          {!selected.transferable ? (
            <p className="readout rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-[10px] text-danger">
              This token is not transferable — the issuer minted it without the transferable flag.
            </p>
          ) : null}
          <input
            value={destination}
            onChange={(e) => {
              setDestination(e.target.value);
              setValid(null);
            }}
            onBlur={async () => setValid(await isValidAddress(destination.trim()))}
            spellCheck={false}
            placeholder="Recipient r-address"
            className="readout h-11 w-full rounded-lg border border-border bg-background px-3 text-[12px] outline-none placeholder:text-faint focus:border-accent"
          />
          {valid === false ? (
            <p className="readout text-[10px] text-danger">Not a valid XRPL address.</p>
          ) : valid ? (
            <p className="readout text-[10px] text-success">
              Fingerprint {addressFingerprint(destination.trim())} ·{" "}
              {truncateAddress(destination.trim(), 6, 6)}
            </p>
          ) : null}
          <button
            type="button"
            disabled={!valid || !selected.transferable}
            onClick={() => setSheetOpen(true)}
            className="readout h-11 w-full rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink disabled:opacity-40"
          >
            Review transfer
          </button>
        </section>
      ) : null}

      <SignSheet
        open={sheetOpen}
        title="Sign NFT transfer"
        checksum={valid ? "✓" : "!"}
        busy={busy}
        rows={[
          { label: "Operation", value: "NFTokenCreateOffer · sell" },
          { label: "NFToken", value: `${selected?.id.slice(0, 16) ?? ""}…` },
          { label: "Recipient", value: destination.trim() },
          {
            label: "Fingerprint",
            value: addressFingerprint(destination.trim()),
            emphasis: "accent",
          },
          { label: "Price", value: "0 XRP (gift)" },
        ]}
        warning="This creates a zero-price offer only the named recipient can accept. It is reversible until they accept — you can cancel the offer."
        onCancel={() => setSheetOpen(false)}
        onConfirm={() => void transfer()}
      />
    </div>
  );
}
